import { Injectable, OnDestroy, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import {
  combineLatest,
  Observable,
  of,
  ReplaySubject,
  Subscription,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
} from 'rxjs';

import { LanguageService } from '@fcom/ui-translate';
import { CmsDataService, ConfigService } from '@fcom/core';
import { MenuItemWithChildren, MenuJson } from '@fcom/common';
import { unsubscribe } from '@fcom/core/utils/unsubscribe';
import { ProfileType } from '@fcom/core-api/login/interfaces/login.interface';
import { finShare } from '@fcom/rx';

import { LoyaltyRoutesPath } from '../interfaces/loyalty.interface';

const EMPTY_MENU: MenuJson = { top: [], main: [], bottom: [], template: '' };

@Injectable()
export class LoyaltyNavigationService implements OnDestroy {
  private cmsDataService = inject(CmsDataService);
  private languageService = inject(LanguageService);
  private router = inject(Router);
  private configService = inject(ConfigService);

  private currentRoute$: ReplaySubject<string> = new ReplaySubject(1);
  private subscriptions = new Subscription();

  constructor() {
    this.subscriptions.add(
      this.router.events
        .pipe(
          filter((routerEvent) => routerEvent instanceof NavigationEnd),
          distinctUntilChanged(),
          map(({ url }: NavigationEnd) => url),
          startWith(this.router.url)
        )
        .subscribe(this.currentRoute$)
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  getMenuItemsForProfileType(profile: ProfileType): Observable<MenuItemWithChildren[]> {
    const isPlus = profile === ProfileType.FPLUS;
    const url = isPlus ? 'fragments.plusNavigation.url' : 'fragments.accountNavigation.url';
    return this.fetchMenu(url, isPlus);
  }

  private fetchMenu(fragmentUrl: string, isPlus: boolean): Observable<MenuItemWithChildren[]> {
    return combineLatest([
      this.languageService
        .translate(fragmentUrl)
        .pipe(switchMap((url) => (!url ? of(EMPTY_MENU) : this.cmsDataService.getFragmentJson(url)))),
      this.currentRoute$,
      this.manageAviosTitle$(isPlus),
    ]).pipe(
      map(([menuJson, currentRoute, manageAviosTitle]: [MenuJson, string, string | null]) => {
        const top = manageAviosTitle ? this.withManageAvios(menuJson.top, manageAviosTitle) : menuJson.top;
        top.forEach((item) => {
          item.active = item.link === currentRoute.replace(/\?.*|(#.*)$/, '');
        });
        return top.filter(({ link }) => !link.includes(LoyaltyRoutesPath.TIER_PROGRESS_AND_BENEFITS));
      }),
      distinctUntilChanged(),
      finShare()
    );
  }

  /**
   * The "Manage Avios" sidebar link is not in the CMS navigation fragment yet, so it is injected
   * from code while `enableBoostAvios` is on (test only — preprod/prod keep the flag off and the
   * CMS fragment unchanged). Returns the localized title to add, or `null` when it should not show.
   */
  private manageAviosTitle$(isPlus: boolean): Observable<string | null> {
    return isPlus && this.configService.cfg.enableBoostAvios
      ? this.languageService.translate('LOYALTY.manageAvios.title')
      : of(null);
  }

  /** Insert the Manage Avios item right after Balance and transactions, matching the design order. */
  private withManageAvios(top: MenuItemWithChildren[], title: string): MenuItemWithChildren[] {
    const item: MenuItemWithChildren = {
      teaserTitle: title,
      link: `/${this.languageService.langValue}/${LoyaltyRoutesPath.PLUS}/${LoyaltyRoutesPath.MANAGE_AVIOS}`,
      external: false,
    };
    const balanceIndex = top.findIndex(({ link }) => link?.includes(LoyaltyRoutesPath.BALANCE_AND_TRANSACTIONS));
    const result = [...top];
    result.splice(balanceIndex >= 0 ? balanceIndex + 1 : result.length, 0, item);
    return result;
  }
}
